<template>
  <v-dialog v-model="value" max-width="900px" persistent scrollable :fullscreen="$vuetify.breakpoint.smAndDown">
    <v-card class="pa-2 pa-sm-4">
      <v-card-title class="py-3 py-sm-4 px-4 px-sm-6 d-flex align-center">
        <v-icon color="blue-grey darken-4" class="mr-2 sm:mr-3">
          {{ isInvoiceMode ? 'mdi-file-send-outline' : 'mdi-bell-ring-outline' }}
        </v-icon>
        <span class="text-subtitle-1 sm:text-h6 font-bold text-blue-grey-darken-4">
          {{
            isInvoiceMode
              ? `Send Invoice Notification (${prefillConfig.invoiceNumber || 'Invoice'})`
              : 'Manual Notification Dispatcher'
          }}
        </span>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-3 pa-sm-6 mt-4 bg-gray-50">
        <div class="p-2 bg-gray-50">
          <v-row no-gutters>
            <!-- Step 1: Usecase (hidden in invoice mode) & Channel -->
            <v-col cols="12" md="6" v-if="!isInvoiceMode">
              <label class="text-caption font-bold text-gray-600 uppercase mb-1 d-block">1. Target Module
                (Usecase)</label>
              <v-select v-model="form.useCase" :items="useCases" item-text="name" item-value="id" label="Select Usecase"
                outlined dense hide-details class="rounded-lg bg-white" @change="onUseCaseChange"
                prepend-inner-icon="mdi-briefcase-outline" :disabled="isPrefillMode"></v-select>
            </v-col>

            <v-col cols="12" :md="isInvoiceMode ? 12 : 6">
              <label class="text-caption font-bold text-gray-600 uppercase mb-1 d-block">
                {{ isInvoiceMode ? '1. Communication Channel' : '2. Communication Channel' }}
              </label>
              <v-select v-model="form.channel" :items="channels" label="Select Channel" outlined dense hide-details
                class="rounded-lg bg-white" @change="onChannelChange" prepend-inner-icon="mdi-message-text-outline"
                :disabled="isInvoiceMode"></v-select>
            </v-col>

            <v-col cols="12" class="pt-0 pb-2" v-if="selectedChannelQuotaReached">
              <v-alert type="error" outlined dense class="rounded-lg mb-0 text-caption">
                <div class="font-bold">Monthly limit reached for {{ form.channel }}!</div>
                <div>
                  Used: <strong>{{ quotaUsage[form.channel].used }}</strong> / Limit:
                  <strong>{{ quotaUsage[form.channel].limit }}</strong>. You will not be able to dispatch new
                  notifications on this channel.
                </div>
              </v-alert>
            </v-col>

            <!-- Step 2: Template Selection -->
            <v-col cols="12">
              <div class="d-flex align-center justify-space-between mb-1">
                <label class="text-caption font-bold text-gray-600 uppercase">
                  {{ isInvoiceMode ? '2. Message Template' : '3. Message Template' }}
                </label>
                <v-btn v-if="selectedTemplate" x-small text color="blue darken-4" @click="showPreview = !showPreview"
                  class="font-bold px-2 rounded-lg">
                  <v-icon left x-small>{{
                    showPreview ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
                  }}</v-icon>
                  {{ showPreview ? 'Hide Preview' : 'Show Preview' }}
                </v-btn>
              </div>
              <v-select v-model="form.templateId" :items="filteredTemplates" item-text="name" item-value="_id"
                label="Choose Template" outlined dense hide-details class="rounded-lg bg-white"
                :disabled="!form.channel" prepend-inner-icon="mdi-file-document-outline">
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center py-2">
                    <v-icon small class="mr-2">{{ getChannelIcon(item.type) }}</v-icon>
                    <div>
                      <div class="font-bold">{{ item.name }}</div>
                      <div class="text-caption grey--text">{{ item.templateCode }}</div>
                    </div>
                  </div>
                </template>
              </v-select>

              <!-- Inline Template Preview -->
              <v-expand-transition>
                <div v-if="showPreview && selectedTemplate" class="mt-4">
                  <v-card outlined class="rounded-xl pa-4 bg-white border-2 border-dashed border-blue-100">
                    <div class="d-flex align-center mb-3">
                      <v-chip x-small :color="getChannelColor(selectedTemplate.type)"
                        class="white--text font-bold mr-2">
                        {{ selectedTemplate.type }}
                      </v-chip>
                      <span class="text-caption font-bold text-gray-700">{{
                        selectedTemplate.name
                      }}</span>
                    </div>
                    <div class="preview-content-box rounded-lg pa-3 bg-gray-50 text-caption"
                      v-html="selectedTemplate.content || selectedTemplate.letterContent"></div>
                  </v-card>
                </div>
              </v-expand-transition>
            </v-col>

            <!-- Step 3: Customer Search / Prefilled Card -->
            <v-col cols="12">
              <label class="text-caption font-bold text-gray-600 uppercase mb-1 d-block">
                {{ isInvoiceMode ? '3. Customer & Invoice Details' : '4. Recipients Selection' }}
              </label>
              <div class="d-flex mb-4 gap-2" v-if="!isInvoiceMode && form.useCase !== 'other' && !isPrefillMode">
                <v-btn :input-value="dispatchMode === 'manual'" @click="dispatchMode = 'manual'" outlined
                  :color="dispatchMode === 'manual' ? 'blue darken-4' : 'grey'" class="rounded-lg flex-grow-1">
                  <v-icon left>mdi-account-search-outline</v-icon>
                  Search & Add
                </v-btn>
                <v-btn :input-value="dispatchMode === 'excel'" @click="dispatchMode = 'excel'" outlined
                  :color="dispatchMode === 'excel' ? 'blue darken-4' : 'grey'" class="rounded-lg flex-grow-1">
                  <v-icon left>mdi-file-excel-outline</v-icon>
                  Excel Bulk
                </v-btn>
              </div>

              <!-- Invoice Mode: Locked Customer & Invoice Info Card -->
              <template v-if="isInvoiceMode">
                <v-card outlined class="rounded-xl px-4 py-3 bg-white border border-blue-100 shadow-sm">
                  <div
                    class="text-caption font-bold text-gray-500 uppercase mb-2 d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                      <v-icon small class="mr-1" color="blue darken-2">mdi-account-circle-outline</v-icon>
                      <span>Recipient Customer</span>
                    </div>
                    <v-chip x-small color="blue darken-1" dark class="font-mono">
                      {{ prefillConfig.invoiceNumber || 'INVOICE' }}
                    </v-chip>
                  </div>
                  <div class="d-flex align-center flex-wrap" style="gap: 12px">
                    <span class="text-body-2 font-bold text-slate-800">{{ prefillConfig.customerName }}</span>
                    <v-chip v-if="prefillConfig.customerCrn" x-small label class="font-mono text-[10px] font-bold">
                      CRN: {{ prefillConfig.customerCrn }}
                    </v-chip>
                    <span v-if="prefillConfig.customerEmail"
                      class="text-caption grey--text text--darken-2 d-flex align-center">
                      <v-icon x-small class="mr-1">mdi-email-outline</v-icon>{{ prefillConfig.customerEmail }}
                    </span>
                    <span v-if="prefillConfig.customerMobile"
                      class="text-caption grey--text text--darken-2 d-flex align-center">
                      <v-icon x-small class="mr-1">mdi-phone-outline</v-icon>{{ prefillConfig.customerMobile }}
                    </span>
                    <span v-if="prefillConfig.invoiceTotal !== undefined"
                      class="text-caption font-bold text-emerald-700 d-flex align-center">
                      <v-icon x-small class="mr-1" color="emerald">mdi-currency-inr</v-icon>
                      Total: ₹{{ Number(prefillConfig.invoiceTotal).toLocaleString('en-IN', {
                        minimumFractionDigits: 2
                      }) }}
                    </span>
                  </div>
                </v-card>
              </template>

              <!-- Standard Prefill mode: show locked customer info card -->
              <template v-else-if="isPrefillMode">
                <v-card outlined class="rounded-xl px-4 py-3 bg-white border border-blue-100 shadow-sm">
                  <div class="text-caption font-bold text-gray-500 uppercase mb-2">
                    <v-icon small class="mr-1" color="blue darken-2">mdi-account-circle-outline</v-icon>Recipient
                  </div>
                  <div class="d-flex align-center flex-wrap" style="gap: 8px">
                    <span class="text-body-2 font-bold">{{ prefillConfig.customerName }}</span>
                    <v-chip v-if="prefillConfig.customerCrn" x-small label class="font-bold">{{
                      prefillConfig.customerCrn
                    }}</v-chip>
                    <span v-if="prefillConfig.caseNo"
                      class="text-caption grey--text text--darken-2 d-flex align-center">
                      <v-icon x-small class="mr-1">mdi-file-document-outline</v-icon>Case #{{
                        prefillConfig.caseNo
                      }}
                    </span>
                    <span v-if="prefillConfig.customerEmail"
                      class="text-caption grey--text text--darken-2 d-flex align-center">
                      <v-icon x-small class="mr-1">mdi-email-outline</v-icon>{{ prefillConfig.customerEmail }}
                    </span>
                    <span v-if="prefillConfig.customerMobile"
                      class="text-caption grey--text text--darken-2 d-flex align-center">
                      <v-icon x-small class="mr-1">mdi-phone-outline</v-icon>{{ prefillConfig.customerMobile }}
                    </span>
                  </div>
                </v-card>
              </template>

              <template v-else>
                <v-autocomplete v-model="selectedCustomer" :items="customerSearchResults" :loading="searchingCustomers"
                  :search-input.sync="customerSearchQuery" item-text="name" item-value="_id"
                  label="Search by Name, CRN, Mobile, Email or Case No" outlined dense hide-details
                  class="rounded-lg bg-white" hide-no-data return-object placeholder="Start typing to search..."
                  @change="addCustomer" :disabled="!form.useCase" prepend-inner-icon="mdi-account-search-outline"
                  :filter="() => true">
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title class="font-bold d-flex align-center">
                        {{ item.name }}
                        <v-chip x-small class="ml-2" color="blue lighten-4" text-color="blue darken-4">
                          {{ item.cases.length }} Cases
                        </v-chip>
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption mb-1">
                        {{ item.mobile }} | {{ item.email }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle v-for="c in item.cases" :key="c._id"
                        class="text-caption grey--text text--darken-2">
                        <v-icon x-small>mdi-file-document-outline</v-icon>
                        {{ c.caseNo }} ({{ getCaseStatus(c) }})
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </template>

              <!-- Attachment chooser — only in prefill mode, after template is selected -->
              <v-expand-transition>
                <div v-if="isPrefillMode && form.templateId" class="mt-4">
                  <label class="text-caption font-bold text-gray-600 uppercase mb-2 d-block">
                    <v-icon small class="mr-1">mdi-paperclip</v-icon>Attachments to send
                  </label>
                  <div class="d-flex flex-column gap-2">
                    <!-- The generated letter or invoice (from prefill) -->
                    <v-checkbox v-if="prefillConfig.attachments && prefillConfig.attachments.length"
                      v-model="sendPrefillAttachment" hide-details dense color="blue darken-3" class="mt-0">
                      <template v-slot:label>
                        <div class="d-flex align-center">
                          <v-icon small class="mr-2" color="red">mdi-file-pdf-box</v-icon>
                          <div>
                            <div class="text-body-2 font-bold">
                              {{ prefillConfig.attachments[0].docName }}
                            </div>
                            <div class="text-caption grey--text">
                              {{ isInvoiceMode ? 'Tax Invoice Document' : 'Generated Letter' }}
                            </div>
                          </div>
                        </div>
                      </template>
                    </v-checkbox>

                    <!-- Template's own linked attachments (if any, not in invoice mode) -->
                    <template v-if="!isInvoiceMode && templateLinkedAttachments.length > 0">
                      <v-checkbox v-for="att in templateLinkedAttachments" :key="att._id" v-model="att.include"
                        hide-details dense color="purple darken-3" class="mt-0">
                        <template v-slot:label>
                          <div class="d-flex align-center">
                            <v-icon small class="mr-2" color="purple">mdi-file-pdf-box</v-icon>
                            <div>
                              <div class="text-body-2 font-bold">{{ att.name }}</div>
                              <div class="text-caption grey--text">
                                Template Linked Attachment (Variables will be send based on the
                                default configurations.)
                              </div>
                            </div>
                          </div>
                        </template>
                      </v-checkbox>
                    </template>
                  </div>
                </div>
              </v-expand-transition>

              <template v-if="dispatchMode === 'excel' && !isPrefillMode">
                <v-card outlined class="rounded-xl pa-4 bg-white border-2 border-dashed">
                  <div class="d-flex align-center justify-space-between mb-4">
                    <div class="text-caption font-bold text-gray-700">
                      <v-icon color="success" class="mr-1">mdi-information-outline</v-icon>
                      {{
                        form.useCase === 'other'
                          ? form.channel === 'EMAIL'
                            ? "Upload Excel with 'Name' and 'Email'"
                            : "Upload Excel with 'Name' and 'Number'"
                          : "Upload Excel with 'Customer CRN' and 'Case No'"
                      }}
                    </div>
                    <v-btn small text color="blue darken-2" class="font-bold" @click="downloadSample">
                      <v-icon left small>mdi-download-outline</v-icon>
                      Sample Excel
                    </v-btn>
                  </div>
                  <v-file-input v-model="excelFile" label="Choose Excel File" outlined dense hide-details
                    accept=".xlsx, .xls" prepend-icon="" prepend-inner-icon="mdi-file-upload-outline" class="rounded-lg"
                    @change="onExcelFileChange" :loading="processingExcel"></v-file-input>

                  <v-expand-transition>
                    <div v-if="processingExcel && totalExcelRows > 0 && !directExcelMode" class="mt-4">
                      <div class="d-flex align-center justify-space-between mb-1">
                        <span class="text-caption font-weight-bold grey--text text--darken-3">Exporting
                          Recipients...</span>
                        <span class="text-caption font-weight-bold primary--text">{{ excelProgress }}% ({{
                          processedCount }} / {{ totalExcelRows }})</span>
                      </div>
                      <v-progress-linear v-model="excelProgress" color="success" height="6" rounded
                        striped></v-progress-linear>
                    </div>
                  </v-expand-transition>

                  <v-expand-transition>
                    <div v-if="directExcelMode" class="mt-4">
                      <v-alert type="info" outlined dense class="rounded-lg mb-0 text-caption">
                        <div class="font-bold">Bulk Mode Active</div>
                        <div>
                          Loaded <strong>{{ directExcelRows.length }}</strong> rows from Excel.
                          These will be processed in the background to prevent performance issues.
                        </div>
                      </v-alert>
                    </div>
                  </v-expand-transition>
                </v-card>
              </template>
            </v-col>

            <!-- Selected Customers List (hidden in prefill mode) -->
            <v-col cols="12" v-if="form.recipients.length > 0 && !directExcelMode && !isPrefillMode">
              <div class="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div class="bg-blue-grey lighten-5 px-6 py-3 d-flex align-center justify-space-between border-b">
                  <span class="text-subtitle-2 font-bold blue-grey--text text--darken-4">
                    <v-icon left small>mdi-account-group-outline</v-icon>
                    Selected Recipients ({{ selectedCount }})
                  </span>
                  <v-btn x-small text color="error" class="font-bold rounded-lg"
                    :disabled="!form.recipients || form.recipients.length === 0" @click="form.recipients = []">
                    Clear All
                  </v-btn>
                </div>

                <div class="recipient-list max-h-[400px] overflow-y-auto">
                  <div v-for="(recipient, rIdx) in form.recipients" :key="recipient._id"
                    class="customer-block border-b last:border-0 hover:bg-gray-50 transition-colors">
                    <!-- Customer Row -->
                    <div class="d-flex align-center pa-4">
                      <v-checkbox v-model="recipient.selected" @change="toggleCustomerCases(recipient)" hide-details
                        dense color="success" class="mt-0 mr-4"></v-checkbox>

                      <div class="flex-grow-1">
                        <div class="d-flex align-center flex-wrap gap-2 mb-1">
                          <v-icon v-if="!isRecipientValid(recipient)" color="red" class="mr-1 pulse-animation"
                            small>mdi-alert</v-icon>
                          <span :class="[
                            'text-body-2 font-bold',
                            !isRecipientValid(recipient) ? 'red--text' : ''
                          ]">{{ recipient.name }}</span>
                          <v-chip x-small label class="font-bold bg-gray-100">{{
                            recipient.crn
                          }}</v-chip>

                          <!-- One row info if single case -->
                          <template v-if="recipient.cases.length === 1">
                            <v-chip x-small outlined color="blue-grey lighten-1" class="ml-2">#{{
                              recipient.cases[0].caseNo }}</v-chip>
                            <v-chip x-small :color="getStatusColor(getCaseStatus(recipient.cases[0]))"
                              class="white--text font-bold">{{ getCaseStatus(recipient.cases[0]) }}</v-chip>
                          </template>

                          <span class="text-caption grey--text text--darken-1 ml-auto">
                            <v-icon x-small class="mr-1">mdi-email-outline</v-icon>{{ recipient.email }}
                          </span>
                          <span class="text-caption grey--text text--darken-1 ml-4">
                            <v-icon x-small class="mr-1">mdi-phone-outline</v-icon>{{ recipient.mobile }}
                          </span>
                        </div>
                      </div>

                      <v-btn icon x-small color="error" @click="removeCustomer(rIdx)" class="ml-4">
                        <v-icon small>mdi-close-circle-outline</v-icon>
                      </v-btn>
                    </div>

                    <!-- Multiple Cases (Branches) -->
                    <div v-if="recipient.cases.length > 1"
                      class="ml-12 pb-4 space-y-1 border-l-2 border-dashed border-gray-200 pl-4">
                      <div v-for="c in recipient.cases" :key="c._id" class="case-item d-flex align-center py-1">
                        <v-checkbox v-model="c.selected" @change="updateCustomerSelection(recipient)" hide-details dense
                          color="success" class="mt-0 mr-3"></v-checkbox>
                        <div class="d-flex align-center flex-grow-1">
                          <span class="text-caption font-bold mr-4">Case #{{ c.caseNo }}</span>
                          <v-chip x-small :color="getStatusColor(getCaseStatus(c))"
                            class="white--text font-bold px-2">{{
                              getCaseStatus(c) }}</v-chip>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4 pa-sm-6 bg-white flex flex-col-reverse sm:flex-row justify-end gap-2">
        <v-btn text @click="close" class="px-6 normal-case w-full sm:w-auto"
          :block="$vuetify.breakpoint.smAndDown">Cancel</v-btn>
        <v-btn color="blue darken-4" class="px-8 white--text font-bold normal-case rounded-lg w-full sm:w-auto"
          elevation="0" :disabled="!isFormValid" :loading="sending" @click="sendNotification"
          v-iam-disabled="`global.notifications.write`" :block="$vuetify.breakpoint.smAndDown">
          <v-icon left>mdi-send-outline</v-icon>
          Send to {{ selectedCount }} Recipients
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import * as XLSX from 'xlsx'

export default {
  name: 'SendNotificationDialog',
  props: {
    value: Boolean,
    prefillConfig: {
      type: Object,
      default: null
      // Shape: { useCase, channel, caseId, caseNo, customerId, customerName, attachments }
    }
  },
  created() {
    this.debouncedSearchCustomers = _.debounce(this.searchCustomers, 500)
  },
  data() {
    return {
      sending: false,
      showPreview: false,
      loadingUseCases: false,
      searchingCustomers: false,
      useCases: [
        { id: 'loan_management', name: 'Loan Management' },
        { id: 'collection_management', name: 'Collection Management' },
        { id: 'other', name: 'Other' }
      ],
      dispatchMode: 'manual', // 'manual' or 'excel'
      excelFile: null,
      templates: [],
      customerSearchQuery: '',
      customerSearchResults: [],
      selectedCustomer: null,
      sendPrefillAttachment: true,
      form: {
        useCase: '',
        channel: '',
        templateId: '',
        recipients: []
      },
      processingExcel: false,
      quotaUsage: null,
      loadingQuota: false,
      excelProgress: 0,
      processedCount: 0,
      totalExcelRows: 0,
      directExcelMode: false,
      directExcelRows: []
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.fetchTemplates()
        this.fetchQuotaUsage()
        if (this.prefillConfig) {
          this.applyPrefill()
        }
      } else {
        this.resetForm()
      }
    },
    customerSearchQuery(val) {
      if (val && val.length > 2) {
        this.debouncedSearchCustomers(val)
      }
    },
    dispatchMode(val) {
      if (val === 'manual') {
        this.directExcelMode = false
        this.directExcelRows = []
        this.form.recipients = []
      }
    }
  },
  computed: {
    isInvoiceMode() {
      return !!(
        this.prefillConfig &&
        (this.prefillConfig.isInvoiceMode ||
          this.prefillConfig.type === 'invoice' ||
          this.prefillConfig.invoiceId)
      )
    },
    channels() {
      if (this.isInvoiceMode) {
        return ['EMAIL', 'WHATSAPP']
      }
      return ['EMAIL', 'WHATSAPP', 'SMS', 'VOICE']
    },
    selectedChannelQuotaReached() {
      if (!this.form.channel || !this.quotaUsage) return false
      const usage = this.quotaUsage[this.form.channel]
      return !!(usage && usage.reached)
    },
    filteredTemplates() {
      if (!this.form.channel) return []
      if (this.isInvoiceMode) {
        return this.templates.filter((t) => {
          if (t.forCampaign) return false
          if (t.type !== this.form.channel || t.type === 'ATTACHMENT') return false
          if (t.type === 'WHATSAPP') {
            return t.whatsappStatus === 'APPROVED'
          }
          return true
        })
      }
      if (!this.form.useCase) return []
      return this.templates.filter((t) => {
        if (t.forCampaign) return false
        if (t.type !== this.form.channel || t.type === 'ATTACHMENT') return false
        if (t.module !== this.form.useCase) return false
        if (t.type === 'WHATSAPP') {
          return t.whatsappStatus === 'APPROVED'
        }
        if (t.type === 'SMS') {
          return t.smsStatus === 'APPROVED'
        }
        return true
      })
    },
    selectedTemplate() {
      return this.templates.find((t) => t._id === this.form.templateId)
    },
    selectedCount() {
      if (this.isInvoiceMode) {
        return 1
      }
      if (this.directExcelMode) {
        return this.directExcelRows.length
      }
      return this.form.recipients.reduce(
        (acc, r) => acc + (r.cases ? r.cases.filter((c) => c.selected).length : 0),
        0
      )
    },
    isFormValid() {
      if (this.isInvoiceMode) {
        return (
          this.form.channel &&
          this.form.templateId &&
          !this.selectedChannelQuotaReached
        )
      }
      const recipientCount = this.directExcelMode ? this.directExcelRows.length : this.selectedCount
      return (
        this.form.useCase &&
        this.form.channel &&
        this.form.templateId &&
        recipientCount > 0 &&
        !this.selectedChannelQuotaReached
      )
    },
    isPrefillMode() {
      return !!this.prefillConfig
    },
    templateLinkedAttachments() {
      if (!this.selectedTemplate || !this.selectedTemplate.attachments) return []
      return this.selectedTemplate.attachments.map((att) => {
        // att may be a populated object or just an id string
        if (typeof att === 'object' && att !== null) {
          return { _id: att._id || att, name: att.name || att._id, include: true }
        }
        // id only — try to find in templates list
        const found = this.templates.find((t) => String(t._id) === String(att))
        return { _id: att, name: found?.name || att, include: true }
      })
    }
  },
  methods: {
    // fetchSubCases removed as we use static useCases for now
    applyPrefill() {
      const cfg = this.prefillConfig
      if (!cfg) return
      if (this.isInvoiceMode) {
        this.form.useCase = 'other'
        this.form.channel = cfg.channel || 'EMAIL'
        this.form.templateId = ''
        this.form.recipients = [
          {
            _id: cfg.customerId || cfg.invoiceId || 'invoice_recipient',
            name: cfg.customerName || 'Customer',
            crn: cfg.customerCrn || '',
            email: cfg.customerEmail || '',
            mobile: cfg.customerMobile || '',
            selected: true,
            cases: []
          }
        ]
        this.sendPrefillAttachment = true
        return
      }

      this.form.useCase = cfg.useCase || 'loan_management'
      this.form.channel = cfg.channel || 'EMAIL'
      this.form.templateId = ''
      // Build a locked recipient from the case data
      if (cfg.caseId) {
        this.form.recipients = [
          {
            _id: cfg.customerId || cfg.caseId,
            name: cfg.customerName || 'Customer',
            crn: cfg.customerCrn || '',
            email: cfg.customerEmail || '',
            mobile: cfg.customerMobile || '',
            selected: true,
            cases: [
              {
                _id: cfg.caseId,
                caseNo: cfg.caseNo || '',
                status: 'OPEN',
                selected: true
              }
            ]
          }
        ]
      }
    },
    async fetchTemplates() {
      try {
        const response = await this.$axios.get('/letter-templates?limit=*')
        const allTemplates = response.data?.data || []
        // Extra safeguard: only retain templates where forCampaign is false/falsy
        this.templates = allTemplates.filter((t) => !t.forCampaign)
      } catch (error) {
        console.error('Error fetching templates:', error)
      }
    },
    async fetchQuotaUsage() {
      try {
        this.loadingQuota = true
        const response = await this.$axios.get('/organization/communication-usage')
        if (response.data && response.data.success) {
          this.quotaUsage = response.data.usage
        }
      } catch (error) {
        console.error('Error fetching quota usage:', error)
      } finally {
        this.loadingQuota = false
      }
    },
    async searchCustomers(query) {
      if (!this.form.useCase) return
      try {
        this.searchingCustomers = true
        const response = await this.$axios.get('/customers/search-linked', {
          params: {
            useCase: this.form.useCase,
            search: query
          }
        })
        this.customerSearchResults = response.data?.data || []
      } catch (error) {
        console.error('Error searching customers:', error)
      } finally {
        this.searchingCustomers = false
      }
    },
    addCustomer(customer) {
      if (customer && !this.form.recipients.some((r) => r._id === customer._id)) {
        // Initialize selection state
        const newRecipient = {
          ...customer,
          selected: true,
          cases: (customer.cases || []).map((c) => ({ ...c, selected: true }))
        }
        this.form.recipients.push(newRecipient)
      }
      this.$nextTick(() => {
        this.selectedCustomer = null
      })
    },
    toggleCustomerCases(recipient) {
      recipient.cases.forEach((c) => {
        c.selected = recipient.selected
      })
    },
    updateCustomerSelection(recipient) {
      recipient.selected = recipient.cases.some((c) => c.selected)
    },
    getCaseStatus(c) {
      if (c.loanDetails && c.loanDetails.currentStatus) {
        return c.loanDetails.currentStatus
      }
      return c.status
    },
    getStatusColor(status) {
      const colors = {
        OPEN: 'blue darken-2',
        IN_PROGRESS: 'amber darken-2',
        CLOSED: 'green darken-2',
        RESOLVED: 'green darken-2',
        REJECTED: 'red darken-2',
        DISBURSED: 'green darken-2',
        APPROVED: 'blue darken-2',
        PENDING: 'amber darken-2'
      }
      return colors[status?.toUpperCase()] || 'grey darken-1'
    },
    removeCustomer(index) {
      this.form.recipients.splice(index, 1)
    },
    onUseCaseChange() {
      this.form.recipients = []
      this.customerSearchResults = []
      this.showPreview = false
      this.excelFile = null
      if (this.form.useCase === 'other') {
        this.dispatchMode = 'excel'
      } else {
        this.dispatchMode = 'manual'
      }
    },
    async onExcelFileChange(file) {
      if (!file) return

      try {
        this.processingExcel = true
        this.totalExcelRows = 0
        this.processedCount = 0
        this.excelProgress = 0

        const reader = new FileReader()
        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result)
            const workbook = XLSX.read(data, { type: 'array' })
            const sheetName = workbook.SheetNames[0]
            const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName])

            if (rows.length === 0) {
              this.$toast.error('Excel file is empty')
              this.processingExcel = false
              return
            }

            this.totalExcelRows = rows.length

            if (rows.length > 100) {
              this.directExcelMode = true
              this.directExcelRows = rows
              this.form.recipients = []
              this.$toast.success(
                `Successfully loaded ${rows.length} rows for background processing.`
              )
              this.excelFile = null
              this.processingExcel = false
              return
            }

            const batchSize = 1000
            const foundRecipients = []

            for (let i = 0; i < rows.length; i += batchSize) {
              const batchRows = rows.slice(i, i + batchSize)

              if (this.form.useCase === 'other') {
                batchRows.forEach((row, idx) => {
                  const name = row['Name'] || row['name'] || `Recipient ${i + idx + 1}`
                  const email = row['Email'] || row['email'] || ''
                  const mobile =
                    row['Number'] || row['number'] || row['Mobile'] || row['mobile'] || ''

                  const tempId = `temp_${Date.now()}_${i + idx}`
                  foundRecipients.push({
                    _id: tempId,
                    name,
                    crn: 'N/A',
                    mobile,
                    email,
                    selected: true,
                    cases: [
                      {
                        _id: `case_${tempId}`,
                        caseNo: 'N/A',
                        status: 'OTHER',
                        selected: true
                      }
                    ]
                  })
                })
              } else {
                try {
                  const response = await this.$axios.post('/notifications/manual-bulk-excel', {
                    rows: batchRows
                  })
                  const batchRecipients = response.data?.data?.data || []
                  foundRecipients.push(...batchRecipients)
                } catch (batchErr) {
                  console.error(`Error fetching batch starting at index ${i}:`, batchErr)
                }
              }

              this.processedCount = Math.min(i + batchSize, rows.length)
              this.excelProgress = Math.round((this.processedCount / this.totalExcelRows) * 100)
            }

            if (foundRecipients.length === 0) {
              this.$toast.warning('No matching customers/recipients found')
            } else {
              if (this.form.useCase === 'other') {
                foundRecipients.forEach((r) => {
                  if (
                    !this.form.recipients.some(
                      (existing) =>
                        (r.email && existing.email === r.email) ||
                        (r.mobile && existing.mobile === r.mobile)
                    )
                  ) {
                    this.form.recipients.push(r)
                  }
                })
              } else {
                foundRecipients.forEach((customer) => {
                  this.addCustomer(customer)
                })
              }
              this.$toast.success(`Successfully added ${foundRecipients.length} recipients`)
            }
          } catch (err) {
            console.error('Error parsing sheet data:', err)
            this.$toast.error('Failed to parse Excel file content')
          } finally {
            this.excelFile = null
            this.processingExcel = false
          }
        }
        reader.readAsArrayBuffer(file)
      } catch (error) {
        console.error('Error processing Excel file upload:', error)
        this.$toast.error('Failed to upload Excel file')
        this.processingExcel = false
      }
    },
    onChannelChange() {
      this.form.templateId = ''
      // Re-validate all selected recipients when channel changes
      this.form.recipients.forEach((r) => {
        if (!this.isRecipientValid(r)) {
          r.selected = false
          if (r.cases) {
            r.cases.forEach((c) => (c.selected = false))
          }
        }
      })
    },
    isRecipientValid(recipient) {
      if (!this.form.channel) return true
      if (this.form.channel === 'EMAIL') {
        return !!recipient.email
      }
      if (
        this.form.channel === 'WHATSAPP' ||
        this.form.channel === 'SMS' ||
        this.form.channel === 'VOICE'
      ) {
        return !!recipient.mobile
      }
      return true
    },
    getChannelIcon(type) {
      const icons = {
        EMAIL: 'mdi-email-outline',
        WHATSAPP: 'mdi-whatsapp',
        SMS: 'mdi-message-text-outline',
        VOICE: 'mdi-phone-in-talk-outline'
      }
      return icons[type] || 'mdi-bell-outline'
    },
    getChannelColor(type) {
      const colors = {
        EMAIL: 'blue',
        WHATSAPP: 'green',
        SMS: 'orange',
        VOICE: 'indigo'
      }
      return colors[type] || 'grey'
    },
    async sendNotification() {
      try {
        this.sending = true

        const payload = {
          useCase: this.form.useCase || 'other',
          channel: this.form.channel,
          templateId: this.form.templateId
        }

        if (this.isInvoiceMode) {
          payload.recipients = [
            {
              name: this.prefillConfig.customerName,
              email: this.prefillConfig.customerEmail,
              mobile: this.prefillConfig.customerMobile,
              customerId: this.prefillConfig.customerId,
              invoiceId: this.prefillConfig.invoiceId
            }
          ]
        } else if (this.directExcelMode) {
          payload.excelRows = this.directExcelRows
          payload.isBulkExcel = true
        } else {
          // Filter selected recipients and cases
          const selectedRecipients = this.form.recipients
            .filter((r) => r.cases && r.cases.some((c) => c.selected))
            .map((r) => ({
              ...r,
              cases: r.cases.filter((c) => c.selected)
            }))

          if (selectedRecipients.length === 0 && this.form.useCase !== 'other') {
            this.$toast.error('Please select at least one recipient/case')
            this.sending = false
            return
          }

          if (this.form.useCase === 'other') {
            payload.recipients = this.form.recipients.map((r) => ({
              name: r.name,
              email: r.email,
              mobile: r.mobile
            }))
          } else {
            payload.recipientIds = selectedRecipients.map((r) => r._id)
            payload.caseIds = selectedRecipients.flatMap((r) => r.cases.map((c) => c._id))
          }
        }

        // Pass custom attachments if provided via prefill (per user checkbox selection)
        if (this.isInvoiceMode) {
          if (this.sendPrefillAttachment && this.prefillConfig?.attachments?.length) {
            payload.attachments = this.prefillConfig.attachments
          }
        } else if (this.isPrefillMode) {
          const chosen = []
          if (this.sendPrefillAttachment && this.prefillConfig?.attachments?.length) {
            chosen.push(...this.prefillConfig.attachments)
          }
          const linkedChosen = this.templateLinkedAttachments
            .filter((a) => a.include)
            .map((a) => ({
              templateId: a._id,
              isTemplate: true,
              docName: a.name.endsWith('.pdf') ? a.name : `${a.name}.pdf`
            }))
          chosen.push(...linkedChosen)
          if (chosen.length) payload.attachments = chosen
        }

        await this.$axios.post('/notifications/manual-bulk', payload)

        if (this.isInvoiceMode && this.prefillConfig?.invoiceId) {
          try {
            await this.$axios.post(`/invoice/${this.prefillConfig.invoiceId}/deliver`, {
              channel: this.form.channel.toLowerCase(),
              to:
                this.form.channel === 'EMAIL'
                  ? this.prefillConfig.customerEmail
                  : this.prefillConfig.customerMobile
            })
          } catch (delErr) {
            console.warn('Failed to update invoice delivery status:', delErr)
          }
        }

        this.$toast.success(
          this.isInvoiceMode
            ? `Invoice notification sent via ${this.form.channel}`
            : this.directExcelMode
              ? `Bulk notifications triggered for ${this.directExcelRows.length} recipients`
              : `Successfully sent notifications to ${this.selectedCount} recipients`
        )
        this.$emit('sent', {
          invoiceId: this.prefillConfig?.invoiceId,
          channel: this.form.channel
        })
        this.close()
      } catch (error) {
        console.error('Error sending notifications:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to send notifications')
      } finally {
        this.sending = false
      }
    },
    async downloadSample() {
      try {
        let data = []
        let filename = 'bulk_notification_sample.xlsx'
        if (this.form.useCase === 'other') {
          if (this.form.channel === 'EMAIL') {
            data = [
              {
                Name: 'John Doe',
                Email: 'john@example.com'
              }
            ]
            filename = 'bulk_email_other_sample.xlsx'
          } else if (this.form.channel === 'VOICE') {
            data = [
              {
                Name: 'John Doe',
                Number: '9876543210'
              }
            ]
            filename = 'bulk_voice_other_sample.xlsx'
          } else {
            data = [
              {
                Name: 'John Doe',
                Number: '919876543210'
              }
            ]
            filename = 'bulk_mobile_other_sample.xlsx'
          }
        } else {
          data = [
            {
              'Customer CRN': 'CRN001',
              'Case No': 'CASE123'
            }
          ]
          filename = 'bulk_notification_sample.xlsx'
        }
        const worksheet = XLSX.utils.json_to_sheet(data)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sample')
        XLSX.writeFile(workbook, filename)
      } catch (error) {
        console.error('Error generating sample:', error)
        this.$toast.error('Failed to generate sample file')
      }
    },
    close() {
      this.$emit('input', false)
    },
    resetForm() {
      this.form = {
        useCase: '',
        channel: '',
        templateId: '',
        recipients: []
      }
      this.customerSearchResults = []
      this.customerSearchQuery = ''
      this.excelProgress = 0
      this.processedCount = 0
      this.totalExcelRows = 0
      this.directExcelMode = false
      this.directExcelRows = []
      this.sendPrefillAttachment = true
    }
  }
}
</script>

<style scoped>
.preview-content-box {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
}

.gap-2 {
  gap: 8px;
}

.pulse-animation {
  animation: pulse-red 2s infinite;
}

@keyframes pulse-red {
  0% {
    transform: scale(0.95);
    opacity: 0.7;
  }

  70% {
    transform: scale(1.1);
    opacity: 1;
  }

  100% {
    transform: scale(0.95);
    opacity: 0.7;
  }
}
</style>
